/**
 * ثبت برش — شروعِ زنجیرهٔ تولید.
 * شمارهٔ بیجک (سند) و کد بیجک = کد تولید جدا هستند؛ هر بیجک چند ردیف محصول
 * (محصول | رنگ | سایز) دارد و تعدادِ برش مبنای مقایسهٔ مراحل بعد است.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, Col, Descriptions, Divider, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography, message } from "antd";
import Alert from "../../components/GxAlert";
import DraftBar from "../../components/DraftBar";
import { useDraft } from "../../utils/draft";
import {
  CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SaveOutlined,
} from "@ant-design/icons";
import {
  createProductionOption, cutBijackDeleteImpact, deleteCutBijack,
  deleteCutterJob, getCutBijacks, getCutRates, checkCutBijackNo, bulkUpdateBijacks,
  renameBijackCode,
  getEmployees, getProductionOptions, saveCutBijack, updateCutBijack,
} from "../../api/client";
import { JalaliDateInput, todayISO, toJalali, fmtNum, JALALI_MONTHS } from "../../utils/dateUtils";
import { byStr, byDate } from "../../utils/sorters";
import {
  PRODUCTION_STAGES, STAGE_MAP, STAGE_RANK, groupStage,
} from "../../utils/productionStatus";
import {
  usePayrollMonthOptions, splitPayrollMonth, joinPayrollMonth, payrollMonthLabel,
} from "../../utils/payrollMonth";
import { formatMoney } from "../../utils/money";
import DismissibleHelp from "../../components/DismissibleHelp";
import { useMonthScope } from "../../components/MonthScope";
import EditableTable from "../../components/EditableTable";
import FormulaNumber from "../../components/FormulaNumber";
import { useAuth } from "../../context/AuthContext";

const { Title, Text } = Typography;
const UNITS_PER_JIN = 12;
const rial = (v) => formatMoney(v, { unit: true });
const rowTotal = (r) => (Number(r.jin_qty) || 0) * UNITS_PER_JIN + (Number(r.extra_units) || 0);

/** تعدادِ کل → جین و تکیِ اضافه. هر جین ۱۲ عدد؛ باقی‌ماندهٔ زیرِ ۱۲ «تکی» است.
 *  (۸۷۵ ⇒ ۷۲ جین + ۱۱ تکی) */
const splitUnits = (total) => {
  const n = Math.max(0, Math.floor(Number(total) || 0));
  return { jin_qty: Math.floor(n / UNITS_PER_JIN), extra_units: n % UNITS_PER_JIN };
};
export const cutLabel = (r) =>
  [r.product_type, r.variant, r.color, r.size_label]
    .filter((x) => x && x !== "—").join(" | ") || "—";

let _rowSeq = 0;
// 🐞 v2.122.0: کلید همیشه **آخر** و تازه — قبلاً `...base` بعد از key می‌آمد و «تکرار ردیف» با `key: undefined`
// ردیفی بی‌کلید می‌ساخت؛ اصل و کپی یک ردیف حساب می‌شدند و ویرایشِ یکی دیگری را هم عوض می‌کرد.
const blankRow = (base = {}) => ({
  product_type: null, variant: null, color: null, size_label: null,
  jin_qty: 0, extra_units: 0, ...base, key: `r${++_rowSeq}`,
});

/** Select با امکانِ افزودنِ گزینهٔ جدید — منو داخل viewport می‌ماند و از کادر بیرون نمی‌زند */
function OptionSelect({ kind, value, onChange, options, onAdded, placeholder, width,
                       parent, disabled, emptyHint }) {
  const [draft, setDraft] = useState("");
  const draftRef = useRef(null);
  const add = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const v = draft.trim();
    if (!v) return;
    try {
      await createProductionOption({ kind, value: v, parent: parent || undefined });
      setDraft("");
      await onAdded();
      onChange(v);
    } catch (err) {
      message.error(err?.response?.data?.detail || "افزودن گزینه ناموفق بود");
    }
  };
  return (
    <Select
      showSearch allowClear value={value} onChange={onChange} placeholder={placeholder}
      /* ⚠️ `open` را کنترل‌شده نکنید: antd روی کلیکِ داخلِ پنل هم
         `onOpenChange(false)` می‌دهد و لیست بسته می‌شود — همان چیزی که
         `preventDefault` می‌خواست جلویش را بگیرد. */
      // هرچه بالا تایپ می‌شود همان‌جا در کادرِ «مورد جدید» می‌نشیند؛ پس کاربر
      // دوباره تایپ نمی‌کند و فقط «افزودن» را می‌زند.
      onSearch={setDraft}
      disabled={disabled}
      notFoundContent={emptyHint || undefined}
      style={{ width: width || "100%", maxWidth: "100%" }}
      optionFilterProp="label" options={options}
      listHeight={220}
      popupMatchSelectWidth={false}
      getPopupContainer={() => document.body}
      styles={{ popup: { root: { maxWidth: "min(360px, 92vw)", zIndex: 1100 } } }}
      popupRender={(menu) => (
        <div style={{ maxWidth: "min(360px, 92vw)" }}>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>{menu}</div>
          <Divider style={{ margin: "6px 0" }} />
          {/* 🐞 «نمی‌شد توی کادرِ مورد جدید تایپ کرد»:
              `preventDefault` لازم است تا کلیک باعثِ blur و بسته‌شدنِ لیست نشود،
              ولی همان جلوی **فوکوس‌گرفتنِ کادرِ متن** را هم می‌گرفت. راهِ درست:
              جلوی رفتارِ پیش‌فرض را بگیر ولی فوکوس را خودمان بدهیم. */}
          <div
            onMouseDown={(e) => { e.preventDefault(); draftRef.current?.focus(); }}
            style={{
              display: "flex", gap: 6, padding: "0 8px 8px", alignItems: "center", flexWrap: "wrap",
            }}>
            <Input size="small" ref={draftRef} value={draft} placeholder="مورد جدید" autoComplete="off"
              onChange={(e) => setDraft(e.target.value)} onPressEnter={add}
              // وگرنه کلیدها را خودِ Select می‌قاپد (فاصله، جهت‌نما، Enter)
              onKeyDown={(e) => e.stopPropagation()}
              style={{ flex: "1 1 120px", minWidth: 0 }} />
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={add}
              style={{ flex: "0 0 auto" }}>
              افزودن
            </Button>
          </div>
        </div>
      )}
    />
  );
}

export default function CutRegisterPage() {
  const monthOpts = usePayrollMonthOptions();      // ماه‌های حقوقِ بسته غیرفعال (v2.152.12)
  const [opts, setOpts] = useState({ product: [], color: [], size: [], variant: [] });
  const [employees, setEmployees] = useState([]);
  const [cutRates, setCutRates] = useState([]);
  const [bijacks, setBijacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // فرمِ بیجک
  // بدونِ مجوزِ مبالغ، هیچ فی/حقوقی در این صفحه نمایش داده نمی‌شود.
  const { canViewAmounts } = useAuth();
  const money = canViewAmounts("production");
  const [editingCode, setEditingCode] = useState(null);
  // دیپ‌لینکِ ?edit=<کد تولید> — از صفحهٔ «ثبت و مدیریت تولید» مستقیم می‌آید اینجا
  const [searchParams, setSearchParams] = useSearchParams();
  const editRef = useRef(null);
  const [delTarget, setDelTarget] = useState(null);   // {code, orderId, label, impact}
  const [delPin, setDelPin] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [bijackNo, setBijackNo] = useState("");
  // شماره بیجک یکتاست — نتیجهٔ چکِ لحظه‌ای: null یا {production_code, products_count}
  const [dupBijack, setDupBijack] = useState(null);
  // v2.145.0 — تکراری‌بودنِ شمارهٔ بیجکِ هر ردیف (کلید = row.key)
  const [rowDup, setRowDup] = useState({});
  const [sewFilter, setSewFilter] = useState(null);   // فیلترِ وضعیتِ دوختِ لیست
  // دورهٔ ماهانهٔ لیست — per user یادش می‌ماند؛ «همه» هم دارد
  const monthScope = useMonthScope("cut-register");
  // کدِ تولید فقط تا وقتی کاری رویش ثبت نشده قابلِ اصلاح است
  const [editingHasWork, setEditingHasWork] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [code, setCode] = useState("");
  const [entryDate, setEntryDate] = useState(todayISO());
  const [cutter, setCutter] = useState(null);
  // ماهِ محاسبهٔ حقوق — خالی یعنی طبقِ تاریخِ برش
  const [payrollMonth, setPayrollMonth] = useState(undefined);
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([blankRow()]);

  const loadOptions = useCallback(async () => {
    const r = await getProductionOptions({ active_only: true });
    const grouped = { product: [], color: [], size: [], variant: [] };
    (r.data || []).forEach((o) => {
      if (!grouped[o.kind]) return;
      // «مدل» صاحب دارد؛ باید بدانیم زیرِ کدام محصول است
      grouped[o.kind].push({ value: o.value, label: o.value, parent: o.parent });
    });
    setOpts(grouped);
  }, []);

  const loadBijacks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getCutBijacks();
      setBijacks(r.data || []);
    } catch {
      message.error("خطا در بارگذاری بیجک‌های برش");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadOptions().catch(() => {});
    loadBijacks();
    getEmployees().then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setEmployees(list.filter((e) => e.status !== "inactive"));
    }).catch(() => {});
    getCutRates().then((r) => setCutRates(r.data || [])).catch(() => {});
  }, [loadOptions, loadBijacks]);

  // آمدن با ?edit=<کد> از صفحهٔ تولید ⇒ همان بیجک در فرم باز شود
  useEffect(() => {
    const want = searchParams.get("edit");
    if (!want || !bijacks.length || editRef.current === want) return;
    const g = bijacks.find((b) => String(b.production_code) === String(want));
    if (g) {
      editBijack(g);
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }, [bijacks, searchParams]);   // editBijack عمداً در deps نیست (هر رندر تازه ساخته می‌شود)

  /** چکِ یکتاییِ شماره بیجک — روی blur؛ ویرایشِ همان بیجک مستثناست */
  const checkBijackNo = async () => {
    const v = bijackNo.trim();
    if (!v) { setDupBijack(null); return; }
    try {
      const r = await checkCutBijackNo(v);
      if (r.data?.exists && r.data.production_code !== (editingCode || code.trim() || null)) {
        setDupBijack(r.data);
      } else {
        setDupBijack(null);
      }
    } catch { setDupBijack(null); }
  };

  const checkRowBijack = async (key, val) => {
    const v = (val || "").trim();
    if (!v) { setRowDup((m) => ({ ...m, [key]: null })); return; }
    try {
      const r = await checkCutBijackNo(v);
      const dup = r.data?.exists && r.data.production_code !== (editingCode || code.trim() || null) ? r.data : null;
      setRowDup((m) => ({ ...m, [key]: dup }));
    } catch { /* */ }
  };

  // برشکار فقط از بینِ نقش‌های مرتبط — نه کلِ ۹۰+ پرسنل. job_title متنِ آزاد است،
  // پس کلیدِ «همهٔ پرسنل» هم می‌ماند و نفرِ انتخاب‌شدهٔ فعلی همیشه در لیست است.
  const [allCutters, setAllCutters] = useState(false);
  const cutterList = allCutters ? employees : employees.filter((e) =>
    /برش/.test(e.job_title || "") || e.id === cutter);
  /** مدل‌های ثبت‌شدهٔ یک محصول — مدلِ محصولِ دیگر اینجا دیده نمی‌شود.
   *  مدل‌هایی که در بیجک‌های قبلی به‌کار رفته‌اند ولی هنوز در کاتالوگ نیستند هم
   *  اضافه می‌شوند تا چیزی از قلم نیفتد. */
  const variantOptsFor = useCallback((product) => {
    if (!product) return [];
    const seen = new Map();
    opts.variant.forEach((o) => {
      if (o.parent === product) seen.set(o.value, { value: o.value, label: o.value });
    });
    bijacks.forEach((g) => (g.products || []).forEach((p) => {
      if (p.variant && p.product_type === product && !seen.has(p.variant)) {
        seen.set(p.variant, { value: p.variant, label: p.variant });
      }
    }));
    return [...seen.values()];
  }, [opts.variant, bijacks]);

  const empOpts = employees.map((e) => ({ value: e.id, label: `${e.full_name} (${e.employee_code})` }));
  const cutterOpts = cutterList.map((e) => ({ value: e.id, label: `${e.full_name} (${e.employee_code})` }));

  /** کدِ تولیدی که از قبل ثبت شده — هشدار می‌دهیم ولی جلویش را نمی‌گیریم،
   *  چون واقعاً ممکن است چند محصول زیرِ یک بیجک باشد. */
  const existingGroup = !editingCode && code
    ? bijacks.find((b) => String(b.production_code) === String(code).trim())
    : null;
  // لیستِ بیجک‌ها: اول آماده دوخت، بعد نیمه‌کاره، آخر تمام‌شده‌ها
  // شمارش‌ها روی همان دورهٔ ماهانه — وگرنه دکمه «همه (۱۷)» می‌گوید و لیست ۵ تا
  const scopedBijacks = bijacks.filter((g) => monthScope.presentIn(g.entry_date, g.payroll_year, g.payroll_month));
  const sewCounts = scopedBijacks.reduce((acc, g) => {
    const k = groupStage(g.products);
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const visibleBijacks = [...bijacks]
    .map((g) => ({ ...g, payroll_key: joinPayrollMonth(g.payroll_year, g.payroll_month) }))
    .filter((g) => monthScope.presentIn(g.entry_date, g.payroll_year, g.payroll_month))
    .filter((g) => !sewFilter || groupStage(g.products) === sewFilter)
    .sort((a, b) => (STAGE_RANK[groupStage(a.products)] ?? 9) - (STAGE_RANK[groupStage(b.products)] ?? 9));

  const totalUnits = rows.reduce((a, r) => a + rowTotal(r), 0);
  const totalJin = rows.reduce((a, r) => a + (Number(r.jin_qty) || 0), 0);
  const totalExtra = rows.reduce((a, r) => a + (Number(r.extra_units) || 0), 0);

  const patchRow = (key, patch) =>
    setRows((rs) => rs.map((r) => {
      if (r.key !== key) return r;
      const next = { ...r, ...patch };
      // ردیفی که هویتِ پارتش (محصول/مدل/رنگ) عوض شد و هم‌پارتیِ دیگری دارد ⇒ پارتِ تازه (نه تغییرِ نامِ پارتِ اصلی)
      const identity = ["product_type", "variant", "color"].some((k) => k in patch && patch[k] !== r[k]);
      if (identity && r.order_id && rs.some((x) => x.key !== key && x.order_id === r.order_id)) {
        delete next.order_id;
      }
      return next;
    }));

  /* v2.142.0 — پیش‌نویسِ خودکارِ «بیجکِ جدید» (فقط ثبتِ تازه؛ ویرایشِ بیجکِ ذخیره‌شده از سرور خوانده می‌شود) */
  const cutDraftValue = useMemo(() => ({ bijackNo, code, entryDate, cutter, payrollMonth, notes, rows }),
    [bijackNo, code, entryDate, cutter, payrollMonth, notes, rows]);
  const cutDraft = useDraft("cut:new", cutDraftValue, {
    enabled: !editingCode,
    isEmpty: (v) => !v || (!(v.bijackNo || "").trim()
      && !(v.rows || []).some((r) => r.product_type || Number(r.jin_qty) || Number(r.extra_units))),
  });
  const restoreCutDraft = (v) => {
    if (!v) return;
    setBijackNo(v.bijackNo || ""); setCode(v.code || ""); setEntryDate(v.entryDate || todayISO());
    setCutter(v.cutter ?? null); setPayrollMonth(v.payrollMonth); setNotes(v.notes || "");
    setRows(v.rows && v.rows.length ? v.rows.map((r) => blankRow(r)) : [blankRow()]);
  };

  const resetForm = () => {
    setEditingCode(null); setEditingHasWork(false);
    setBijackNo(""); setCode(""); setEntryDate(todayISO());
    setCutter(null); setNotes(""); setRows([blankRow()]); setPayrollMonth(undefined);
  };

  /** ذخیرهٔ ویرایشِ دسته‌جمعیِ لیست — سرگروهِ بیجک‌ها (تراکنشی در سرور). */
  const saveBijackEdits = async (items) => {
    const payload = items.map(({ id, payroll_key, production_code, ...rest }) => ({
      production_code: String(id),                       // کدِ فعلی (کلیدِ ردیف)
      // کدِ تازه جدا فرستاده می‌شود تا سرور همان نگهبان‌های ویرایشِ تکی را بزند
      ...(production_code && String(production_code).trim() !== String(id)
        ? { new_production_code: String(production_code).trim() } : {}),
      ...rest,
      ...(payroll_key !== undefined ? splitPayrollMonth(payroll_key) : {}),
    }));
    await bulkUpdateBijacks(payload);
    await loadBijacks();
  };

  const applyCodeRename = async () => {
    const next = (code || "").trim();
    if (!next || next === editingCode) return;
    setRenaming(true);
    try {
      const r = await renameBijackCode(editingCode, next);
      message.success(r.data?.message || "کد تولید اصلاح شد");
      setEditingCode(next);
      editRef.current = next;
      await loadBijacks();
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : (d?.message || "اصلاحِ کد ناموفق بود"));
      setCode(editingCode);      // برگرداندن به مقدارِ واقعی
    }
    setRenaming(false);
  };

  const editBijack = (g) => {
    setEditingCode(g.production_code);
    setEditingHasWork(!!g.has_work);
    // v2.145.0 — شمارهٔ هر محصول در ردیفِ خودش؛ بالای فرم فقط اگر همه یکی باشند
    const bnos = [...new Set((g.products || []).map((p) => p.bijack_no).filter(Boolean))];
    setBijackNo(bnos.length === 1 ? bnos[0] : "");
    setRowDup({});
    setCode(g.production_code);
    setEntryDate(g.entry_date || todayISO());
    setCutter(g.cutter_employee_id || null);
    setPayrollMonth(joinPayrollMonth(g.payroll_year, g.payroll_month));
    setNotes(g.notes || "");
    const next = [];
    (g.products || []).forEach((p) => (p.sizes || []).forEach((s) => next.push(blankRow({
      order_id: p.order_id,          // ⇐ ویرایشِ همان ردیف، حتی با تغییرِ نام محصول/رنگ
      bijack_no: bnos.length === 1 ? "" : (p.bijack_no || ""),
      product_type: p.product_type, variant: p.variant, color: p.color,
      size_label: s.size_label,
      jin_qty: s.jin_qty, extra_units: s.extra_units,
    }))));
    setRows(next.length ? next : [blankRow()]);
    window.scrollTo({ top: 0, behavior: "smooth" });
    editRef.current = g.production_code;
  };

  const askDelete = async (code, orderId, label) => {
    try {
      const { data } = await cutBijackDeleteImpact(code, orderId);
      setDelTarget({ code, orderId, label, impact: data });
      setDelPin("");
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در بررسیِ حذف"); }
  };

  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      const { data } = await deleteCutBijack(delTarget.code,
        { orderId: delTarget.orderId, adminPin: delPin || undefined });
      message.success(data.message || "حذف شد");
      if (editingCode === delTarget.code && !delTarget.orderId) resetForm();
      setDelTarget(null);
      await loadBijacks();
    } catch (e) { message.error(e?.response?.data?.detail || "حذف ناموفق بود"); }
    setDeleting(false);
  };

  const submit = async () => {
    const clean = rows.filter((r) => r.product_type && rowTotal(r) > 0);
    if (!clean.length) return message.warning("حداقل یک ردیف محصول با تعداد لازم است");
    // v2.145.0 — هر ردیف شمارهٔ بیجکِ خودش را دارد؛ خالی ⇒ شمارهٔ بالای فرم
    if (!bijackNo.trim() && clean.some((r) => !(r.bijack_no || "").trim())) {
      return message.warning("شمارهٔ بیجکِ هر ردیف را بنویسید (یا شمارهٔ بالای فرم را پر کنید تا برای ردیف‌های خالی استفاده شود)");
    }
    const payload = {
      bijack_no: bijackNo.trim() || null,
      production_code: code.trim() || null,
      entry_date: entryDate,
      ...splitPayrollMonth(payrollMonth),
      cutter_employee_id: cutter || null,
      notes: notes || null,
      rows: clean.map((r) => ({
        order_id: r.order_id || null,
        bijack_no: (r.bijack_no || "").trim() || null,
        product_type: r.product_type, variant: r.variant || null, color: r.color || null,
        size_label: r.size_label || null,
        jin_qty: Number(r.jin_qty) || 0, extra_units: Number(r.extra_units) || 0,
      })),
    };
    setSaving(true);
    try {
      const r = editingCode
        ? await updateCutBijack(editingCode, payload)
        : await saveCutBijack(payload);
      const out = r.data || {};
      message.success(out.message || "برش ثبت شد");
      if (out.kept_orders?.length) {
        message.warning(`ردیف‌های ${out.kept_orders.join("، ")} چون کار ثبت‌شده دارند حذف نشدند — اول در صفحهٔ دوختِ همان محصول «پاک‌کردنِ کلِ دوخت» را بزنید، بعد دوباره ذخیره کنید`, 8);
      }
      if (!editingCode) cutDraft.clear();          // ثبت شد ⇒ پیش‌نویس لازم نیست
      setEditingCode(out.production_code);
      setCode(out.production_code);
      await loadBijacks();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // شماره بیجکِ تکراری — پاپ‌آپ با گزینهٔ «رفتن به همان بیجک»
      if (e?.response?.status === 409 && d?.code === "duplicate_bijack_no") {
        setDupBijack(d);
        Modal.confirm({
          title: "شماره بیجک تکراری است",
          content: `شماره بیجک ${d.bijack_no} قبلاً با کد تولید ${d.production_code} `
            + `(${fmtNum(d.products_count)} محصول) ثبت شده. می‌خواهید همان بیجک را باز کنید؟`,
          okText: "رفتن به همان بیجک",
          cancelText: "تغییر شماره",
          onOk: () => {
            const g = bijacks.find((b) => b.production_code === d.production_code);
            if (g) { editBijack(g); setDupBijack(null); }
          },
        });
      } else {
        message.error(typeof d === "string" ? d : (d?.message || "ثبت برش ناموفق بود"));
      }
    }
    setSaving(false);
  };

  const current = useMemo(
    () => bijacks.find((g) => g.production_code === editingCode) || null,
    [bijacks, editingCode]);

  const rowCols = [
    /* v2.145.0 — کاربر: «یک کدِ تولید ممکن است محصولِ زیادی داشته باشد و در چند بیجک وارد شود؛ شمارهٔ بیجک را بیاور در ستونِ
       ردیف‌های محصول که برای هر محصول جداگانه وارد کنیم». خالی ⇒ همان شمارهٔ بالای فرم. */
    {
      title: "شماره بیجک", dataIndex: "bijack_no", width: 130,
      render: (v, r) => (
        <div>
          <Input size="small" value={v || ""} placeholder={bijackNo.trim() ? `همان ${bijackNo.trim()}` : "شماره بیجک"}
            status={rowDup[r.key] ? "error" : undefined}
            onChange={(e) => patchRow(r.key, { bijack_no: e.target.value })}
            onBlur={(e) => checkRowBijack(r.key, e.target.value)} />
          {rowDup[r.key] && (
            <Text type="danger" style={{ fontSize: 11 }}>با کد {rowDup[r.key].production_code} تکراری است</Text>
          )}
        </div>
      ),
    },
    {
      title: "محصول", dataIndex: "product_type", width: 210,
      render: (v, r) => (
        <OptionSelect kind="product" value={v} options={opts.product} placeholder="محصول"
          // عوض‌شدنِ محصول ⇒ مدلِ قبلی بی‌معنی می‌شود (مدل‌ها per-محصول‌اند)
          onChange={(x) => patchRow(r.key, { product_type: x, variant: null })}
          onAdded={loadOptions} />
      ),
    },
    {
      // «مدل» فقط وقتی لازم است که یک سطرِ نرخ‌نامه چند محصولِ هم‌نرخ را پوشش دهد
      // (مثلِ «رکابی و تاپ پنبه»). خالی‌گذاشتنش کاملاً مجاز است.
      title: (
        <Space size={4}>
          <span>مدل</span>
          <Tooltip title="اگر یک ردیفِ نرخ‌نامه چند محصولِ هم‌نرخ دارد (مثلاً «رکابی و تاپ»)، اینجا مشخص کنید کدام است. خالی هم اشکالی ندارد.">
            <span style={{ color: "var(--text-sec)", cursor: "help" }}>؟</span>
          </Tooltip>
        </Space>
      ),
      dataIndex: "variant", width: 140,
      render: (v, r) => (
        // مدل زیرِ همان محصول تعریف می‌شود؛ تا محصول انتخاب نشده غیرفعال است
        <OptionSelect kind="variant" parent={r.product_type} value={v}
          options={variantOptsFor(r.product_type)}
          disabled={!r.product_type}
          placeholder={r.product_type ? "اختیاری" : "اول محصول"}
          emptyHint="مدلی برای این محصول ثبت نشده — پایین بنویسید و «افزودن» بزنید"
          onChange={(x) => patchRow(r.key, { variant: x })} onAdded={loadOptions} />
      ),
    },
    {
      title: "رنگ", dataIndex: "color", width: 130,
      render: (v, r) => (
        <OptionSelect kind="color" value={v} options={opts.color} placeholder="رنگ"
          onChange={(x) => patchRow(r.key, { color: x })} onAdded={loadOptions} />
      ),
    },
    {
      title: "سایز", dataIndex: "size_label", width: 110,
      render: (v, r) => (
        <OptionSelect kind="size" value={v} options={opts.size} placeholder="سایز"
          onChange={(x) => patchRow(r.key, { size_label: x })} onAdded={loadOptions} />
      ),
    },
    {
      title: "جین", dataIndex: "jin_qty", width: 90, align: "center",
      render: (v, r) => (
        <InputNumber min={0} value={v} style={{ width: "100%" }}
          onChange={(x) => patchRow(r.key, { jin_qty: x || 0 })} />
      ),
    },
    {
      title: "تکی اضافه", dataIndex: "extra_units", width: 100, align: "center",
      render: (v, r) => (
        <InputNumber min={0} value={v} style={{ width: "100%" }}
          // ورودیِ ≥۱۲ به جین سرریز می‌شود تا «تکی» همیشه زیرِ ۱۲ بماند
          onChange={(x) => {
            const extra = Math.max(0, Math.floor(Number(x) || 0));
            if (extra < UNITS_PER_JIN) return patchRow(r.key, { extra_units: extra });
            patchRow(r.key, {
              jin_qty: (Number(r.jin_qty) || 0) + Math.floor(extra / UNITS_PER_JIN),
              extra_units: extra % UNITS_PER_JIN,
            });
          }} />
      ),
    },
    {
      // تعدادِ کل مستقیم وارد می‌شود و خودش به جین/تکی می‌شکند —
      // کاربر عددِ روی برگه را دارد، نه تفکیکش را.
      title: "تعداد", width: 110, align: "center",
      render: (_, r) => (
        // مثلِ اکسل: «۱۲۰+۸۰» و Enter ⇒ ۲۰۰، بعد خودش به جین/تکی می‌شکند
        <FormulaNumber min={0} value={rowTotal(r)} placeholder="مثلاً ۱۲۰+۸۰"
          onChange={(x) => patchRow(r.key, splitUnits(x))} />
      ),
    },
    {
      title: "نمایش ردیف", width: 220,
      render: (_, r) => <Text style={{ fontWeight: 600 }}>{cutLabel(r)}</Text>,
    },
    {
      title: "", width: 80,
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title="تکرار ردیف">
            <Button size="small" icon={<CopyOutlined />}
              onClick={() => setRows((rs) => {
                // کپی با کلیدِ تازه، زیرِ همان ردیف. شناسهٔ پارت می‌ماند (سایزِ دیگرِ همان پارت)؛ اگر محصول/مدل/رنگش
                // عوض شود، `patchRow` جدایش می‌کند تا پارتِ تازه شود.
                const i = rs.findIndex((x) => x.key === r.key);
                return [...rs.slice(0, i + 1), blankRow({ ...r }), ...rs.slice(i + 1)];
              })} />
          </Tooltip>
          <Button size="small" danger icon={<DeleteOutlined />} disabled={rows.length === 1}
            onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} />
        </Space>
      ),
    },
  ];

  const listCols = [
    {
      title: "کد تولید", dataIndex: "production_code", width: 180,
      sorter: byStr("production_code"),
      // همان قانونِ ویرایشِ تکی: تا وقتی کاری ثبت نشده قابلِ اصلاح است
      editType: "text",
      editRule: (val, row) => {
        if (!String(val || "").trim()) return "کد تولید نمی‌تواند خالی باشد";
        if (row?.has_work && String(val).trim() !== row.production_code) {
          return "این بیجک کارِ ثبت‌شده دارد؛ کدش قابلِ تغییر نیست";
        }
        return null;
      },
      render: (v, g) => (
        <Space size={4}>
          <Tag color="blue">{v}</Tag>
          {g.has_work && (
            <Tooltip title="کارِ دوخت/کنترل ثبت شده — کد قفل است">
              <Tag>قفل</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: "شماره بیجک", dataIndex: "bijack_no", width: 140,
      sorter: byStr("bijack_no"), editType: "text" },
    {
      // شناسهٔ پایدار به‌جای `__posN`؛ وگرنه با هر تغییرِ تعدادِ ستون‌ها جای
      // این ستون در چیدمانِ ذخیره‌شده می‌لغزید. (در فهرستِ «ستون‌ها» نمی‌آید،
      // چون آن فهرست فقط ستون‌های دارای dataIndex را نشان می‌دهد.)
      title: "وضعیت دوخت", key: "sewing_stage", width: 130,
      render: (_, g) => {
        const st = STAGE_MAP[groupStage(g.products)];
        return <Tag color={st?.color}>{st?.label}</Tag>;
      },
    },
    { title: "تاریخ", dataIndex: "entry_date", width: 115, sorter: byDate("entry_date"),
      editType: "date", render: (v) => toJalali(v) },
    { title: "برشکار", dataIndex: "cutter_employee_id", width: 160,
      sorter: byStr("cutter_name"), editType: "select", options: cutterOpts,
      render: (_, g) => g.cutter_name || "—" },
    /* v2.158.1 (کاربر): «ماهِ محاسبهٔ اینجا = ماهِ حقوقِ برشکار؛ ربطی به دوخت ندارد — ماهِ دوخت از بخشِ خودش در تولید» */
    { title: "ماه حقوق برشکار", dataIndex: "payroll_key", width: 140,
      editType: "select", editAllowClear: true, options: monthOpts,
      render: (_, g) => (payrollMonthLabel(g.payroll_year, g.payroll_month)
        || "طبقِ تاریخِ برش") },
    {
      title: "ردیف‌های محصول", dataIndex: "products", width: 300,
      // امضای render: (مقدار، رکورد) — بدونِ آرگومانِ دوم، `g` تعریف‌نشده بود و
      // کلیکِ حذفِ تک‌محصول بی‌صدا خطا می‌داد.
      render: (products, g) => (
        <Space direction="vertical" size={0}>
          {(products || []).map((p) => (
            <Text key={p.order_id} style={{ fontSize: 12 }}>
              <Tag color={STAGE_MAP[p.sewing_status]?.color} style={{ marginInlineEnd: 4 }}>
                {STAGE_MAP[p.sewing_status]?.label}
              </Tag>
              {[p.product_type, p.variant, p.color].filter(Boolean).join(" | ")}
              {" — "}
              {(p.sizes || []).map((s) => `${s.size_label}: ${fmtNum(s.total_units)}`).join("، ") || "—"}
              <Tooltip title="حذف همین محصول از این بیجک">
                <Button type="text" size="small" danger icon={<DeleteOutlined />}
                  style={{ marginInlineStart: 4 }}
                  onClick={() => askDelete(g.production_code, p.order_id,
                    [p.product_type, p.color].filter(Boolean).join(" | "))} />
              </Tooltip>
            </Text>
          ))}
        </Space>
      ),
    },
    { title: "جمع تعداد", dataIndex: "total_units", width: 100, align: "center", render: fmtNum },
    ...(!money ? [] : [{
      title: "حقوق برشکار", dataIndex: "cutter_amount", width: 150,
      render: (v) => (v ? rial(v) : <Text type="secondary">ثبت نشده</Text>),
    }]),
    {
      title: "", width: 110,
      render: (_, g) => (
        <Space size={4}>
          <Tooltip title="ویرایش این بیجک">
            <Button size="small" icon={<EditOutlined />} onClick={() => editBijack(g)} />
          </Tooltip>
          <Tooltip title="حذف کل این بیجک">
            <Button size="small" danger icon={<DeleteOutlined />}
              onClick={() => askDelete(g.production_code, null, `کل بیجک ${g.production_code}`)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <Title level={5} style={{ margin: 0 }}>
          ثبت برش{" "}
          <Text type="secondary" style={{ fontSize: 12 }}>
            (شروع زنجیره: برش → ضایعات → تولید → کنترل اول → کنترل نهایی)
          </Text>
        </Title>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={loadBijacks} loading={loading}>تازه‌سازی</Button>
          <Button icon={<PlusOutlined />} onClick={resetForm}>بیجک جدید</Button>
        </Space>
      </div>

      <DismissibleHelp id="cut-register" message="شماره بیجک ≠ کد تولید"
        description={"«شماره بیجک» شمارهٔ برگهٔ سند است و یکتاست؛ «کد تولید» روی محصول است و می‌تواند چند محصول بگیرد. "
          + "که همهٔ مراحل بعد با آن پیدا می‌شوند. اگر کد بیجک را خالی بگذارید سیستم می‌سازد. "
          + "تعداد هر ردیف = جین × ۱۲ + تکی اضافه."} />

      {!editingCode && <DraftBar draft={cutDraft} what="بیجکِ برشی" onRestore={restoreCutDraft} />}
      <Card size="small" title={editingCode ? `ویرایش بیجک ${editingCode}` : "مشخصات بیجک"}
        style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>شماره بیجک — پیش‌فرضِ ردیف‌ها (هر ردیف می‌تواند شمارهٔ خودش را داشته باشد)</Text>
            <Input value={bijackNo} onChange={(e) => setBijackNo(e.target.value)}
              placeholder="مثلاً ۱۰۴۸"
              status={dupBijack ? "error" : undefined}
              onBlur={checkBijackNo} />
            {dupBijack && (
              <Text type="danger" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                این شماره قبلاً با کد تولید {dupBijack.production_code} ثبت شده —{" "}
                <a onClick={() => {
                  const g = bijacks.find((b) => b.production_code === dupBijack.production_code);
                  if (g) { editBijack(g); setDupBijack(null); }
                }}>رفتن به همان بیجک</a>
              </Text>
            )}
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>کد تولید</Text>
            {/* کدِ تولید تا وقتی کاری رویش ثبت نشده قابلِ اصلاح است؛ بعدش قفل
                می‌شود چون رکوردهای حقوقی و مقایسه‌ها به همین کد گره خورده‌اند. */}
            <Space.Compact style={{ width: "100%" }}>
              <Input value={code} onChange={(e) => setCode(e.target.value)}
                disabled={!!editingCode && editingHasWork}
                placeholder="خالی = خودکار"
                status={existingGroup ? "warning" : undefined} />
              {!!editingCode && !editingHasWork && code !== editingCode && (
                <Button type="primary" loading={renaming} onClick={applyCodeRename}>
                  اصلاحِ کد
                </Button>
              )}
            </Space.Compact>
            {/* کدِ تکراری خطا نیست — محصولِ تازه کنارِ محصولاتِ قبلی می‌نشیند.
                فقط باید کاربر بداند دارد به یک بیجکِ موجود اضافه می‌کند. */}
            {existingGroup && (
              <Text type="warning" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                این کد از قبل هست ({fmtNum((existingGroup.products || []).length)} محصول) —
                این ثبت <b>کنارِ</b> آن‌ها اضافه می‌شود، چیزی حذف نمی‌شود.{" "}
                <a onClick={() => editBijack(existingGroup)}>ویرایشِ همان بیجک</a>
              </Text>
            )}
          </Col>
          <Col xs={24} md={5}>
            <Text type="secondary" style={{ fontSize: 12 }}>تاریخ برش</Text>
            <div><JalaliDateInput value={entryDate} onChange={setEntryDate} style={{ width: "100%" }} /></div>
          </Col>
          <Col xs={24} md={5}>
            {/* ماهِ محاسبهٔ حقوق — ممکن است برش خرداد باشد ولی حقوقش تیر.
                خالی = همان ماهِ تاریخِ برش. */}
            <Text type="secondary" style={{ fontSize: 12 }}>ماه محاسبهٔ حقوقِ برشکار</Text>
            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
              فقط حقوقِ برشکار — ماهِ دوخت را از «ثبت و مدیریت تولید» بزنید</Text>
            <Select allowClear value={payrollMonth} onChange={setPayrollMonth}
              options={monthOpts} placeholder="طبقِ تاریخِ برش"
              style={{ width: "100%" }} />
          </Col>
          <Col xs={24} md={7}>
            <Space size={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>برشکار</Text>
              <a style={{ fontSize: 11 }} onClick={() => setAllCutters((v) => !v)}>
                {allCutters ? "فقط برشکارها" : "همهٔ پرسنل"}
              </a>
            </Space>
            <Select showSearch allowClear value={cutter} onChange={setCutter} options={cutterOpts}
              optionFilterProp="label"
              placeholder={allCutters ? "انتخاب از همهٔ پرسنل" : "انتخاب برشکار"}
              style={{ width: "100%" }}
              notFoundContent={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  برشکاری با این نام نیست — <a onClick={() => setAllCutters(true)}>همهٔ پرسنل</a>
                </Text>
              } />
          </Col>
          <Col xs={24}>
            <Input.TextArea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="توضیحات (اختیاری)" />
          </Col>
        </Row>
      </Card>

      <Card size="small" title="ردیف‌های محصول" style={{ marginBottom: 12 }}
        extra={
          <Space wrap>
            <Text>جمع: <b>{fmtNum(totalUnits)}</b> عدد ({fmtNum(totalJin)} جین و {fmtNum(totalExtra)} تکی)</Text>
            <Button icon={<PlusOutlined />} onClick={() => setRows((rs) => [...rs, blankRow()])}>
              افزودن ردیف
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={submit}
              style={{ background: "#107C41", borderColor: "#107C41" }}>
              {editingCode ? "ذخیرهٔ تغییرات بیجک" : "ثبت برش"}
            </Button>
          </Space>
        }>
        <Table size="small" rowKey="key" dataSource={rows} columns={rowCols}
          pagination={false} scroll={{ x: "max-content" }} />
      </Card>

      {money && (
      <Card size="small" title="کارکرد و حقوق برشکار" style={{ marginBottom: 12 }}>
        {!editingCode ? (
          <Text type="secondary">اول بیجک را ثبت کنید تا کارکرد برشکار قابل ثبت شود.</Text>
        ) : (
          <>
            {/* v2.115.0: حقوقِ برشکار خودکار از خودِ بیجک — برشکارِ بالای فرم × جمعِ جین/تکی × فیِ برش؛
                تاریخ = «تاریخ برش»، ماه = «ماه محاسبهٔ حقوق» (خالی ⇒ ماهِ تاریخِ برش). ثبتِ جداگانه لازم نیست. */}
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              حقوقِ برشکار <b>خودکار</b> از همین بیجک حساب می‌شود: برشکارِ بالای فرم × جمعِ جین و تکی × فیِ برش
              ({cutRates[0] ? `${fmtNum(cutRates[0].rate_per_jin)} ریال / جین` : "نرخ‌نامه"}). تاریخ = «تاریخ برش»؛
              ماهِ حقوق = «ماه محاسبهٔ حقوق»، و اگر خالی بود ماهِ تاریخِ برش. با هر ذخیرهٔ بیجک به‌روز می‌شود.
            </Text>
            {!current?.cutter_jobs?.length && (
              <Text type="warning" style={{ fontSize: 12 }}>برشکار انتخاب نشده یا تعداد صفر است — حقوقِ برشی ثبت نمی‌شود.</Text>
            )}
            {!!current?.cutter_jobs?.length && (
              <Table size="small" rowKey="id" style={{ marginTop: 10 }} pagination={false}
                dataSource={current.cutter_jobs}
                columns={[
                  { title: "تاریخ", dataIndex: "work_date", render: (v) => toJalali(v) },
                  { title: "ماهِ حقوق", key: "pm",
                    render: (_, j) => (j.payroll_month ? payrollMonthLabel(j.payroll_year, j.payroll_month)
                      : toJalali(j.work_date).slice(0, 7)) },
                  { title: "برشکار", dataIndex: "cutter_name" },
                  { title: "جین", dataIndex: "jin_qty", render: fmtNum },
                  { title: "تکی", dataIndex: "extra_units", render: fmtNum },
                  { title: "تعداد", dataIndex: "total_units", render: fmtNum },
                  ...(!money ? [] : [
                    { title: "فی جین", dataIndex: "rate_per_jin", render: rial },
                    { title: "مبلغ (ریال)", dataIndex: "amount", align: "left", render: (v) => <b className="gx-money">{formatMoney(v)}</b> },
                  ]),
                  {
                    title: "حقوق", dataIndex: "payroll_synced", width: 110,
                    render: (v) => v
                      ? <Tag color="green">به حقوق رفت</Tag>
                      : <Tag color="orange">بدون اتصال</Tag>,
                  },
                  {
                    title: "", width: 50,
                    render: (_, j) => (
                      <Popconfirm title="حذف کارکرد؟ رکورد حقوق هم پاک می‌شود"
                        okText="بله" cancelText="خیر"
                        onConfirm={async () => {
                          await deleteCutterJob(j.order_id, j.id);
                          message.success("حذف شد");
                          loadBijacks();
                        }}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ),
                  },
                ]} />
            )}
          </>
        )}
      </Card>
      )}

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="بیجک‌های ثبت‌شده" value={bijacks.length} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="جمع برش (همهٔ بیجک‌ها)"
              value={bijacks.reduce((a, g) => a + (g.total_units || 0), 0)} />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="بیجک‌های برش ثبت‌شده"
        extra={
          <Space size={4} wrap>
            {monthScope.node}
            <Button size="small" type={!sewFilter ? "primary" : "default"}
              onClick={() => setSewFilter(null)}>
              همه ({fmtNum(scopedBijacks.length)})
            </Button>
            {PRODUCTION_STAGES.map((x) => (
              <Button key={x.value} size="small"
                type={sewFilter === x.value ? "primary" : "default"}
                onClick={() => setSewFilter(sewFilter === x.value ? null : x.value)}>
                {x.label} ({fmtNum(sewCounts[x.value] || 0)})
              </Button>
            ))}
          </Space>
        }>
        {/* EditableTable = ویرایشِ دسته‌جمعی + جابه‌جایی/تغییرِ عرضِ ستون + مرتب‌سازی */}
        <EditableTable
          layoutId="cut-bijacks"
          rowKey="production_code"
          loading={loading}
          dataSource={visibleBijacks}
          columns={listCols}
          onSaveMany={saveBijackEdits}
          /* «ماه محاسبه» تقریباً همیشه همان پیش‌فرض است و فقط سرِ بستنِ ماه
             لمس می‌شود؛ «حقوق برشکار» هم کارِ روزانهٔ ثبتِ برش نیست. هر دو
             از دکمهٔ «ستون‌ها» برمی‌گردند. */
          gxDefaultHidden={["payroll_key", "cutter_amount"]}
          gxLayoutVersion="v2-density"
          scroll={{ x: "max-content" }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }} />
      </Card>

      {/* تأییدِ حذف — دقیقاً می‌گوید چه چیزی پاک می‌شود */}
      <Modal open={!!delTarget} onCancel={() => setDelTarget(null)} destroyOnHidden
        title={`حذف ${delTarget?.label || ""}`}
        okText="بله، حذف کن" cancelText="انصراف" confirmLoading={deleting}
        okButtonProps={{ danger: true, disabled: delTarget?.impact?.has_data && !delPin.trim() }}
        onOk={doDelete}>
        {delTarget?.impact && (
          <div style={{ direction: "rtl" }}>
            <Alert type={delTarget.impact.has_data ? "warning" : "info"} showIcon
              message={delTarget.impact.warning} style={{ marginBottom: 12 }} />
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="ردیف محصول">{fmtNum(delTarget.impact.counts.products)}</Descriptions.Item>
              <Descriptions.Item label="سایزها">{fmtNum(delTarget.impact.counts.sizes)}</Descriptions.Item>
              <Descriptions.Item label="ثبت دوخت">{fmtNum(delTarget.impact.counts.entries)}</Descriptions.Item>
              <Descriptions.Item label="کنترل اول">{fmtNum(delTarget.impact.counts.qc1)}</Descriptions.Item>
              <Descriptions.Item label="کنترل نهایی">{fmtNum(delTarget.impact.counts.qc2)}</Descriptions.Item>
              <Descriptions.Item label="ضایعات">{fmtNum(delTarget.impact.counts.waste)}</Descriptions.Item>
              <Descriptions.Item label="کارکرد برشکار">{fmtNum(delTarget.impact.counts.cutter_jobs)}</Descriptions.Item>
              <Descriptions.Item label="رکورد حقوق">
                {fmtNum(delTarget.impact.payroll_rows)}
                {money && delTarget.impact.payroll_amount > 0 && (
                  <Text type="danger"> — {rial(delTarget.impact.payroll_amount)}</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
            {delTarget.impact.products?.length > 1 && (
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <Text type="secondary">محصولاتی که حذف می‌شوند: </Text>
                {delTarget.impact.products.map((p) => <Tag key={p.order_id}>{p.label}</Tag>)}
              </div>
            )}
            {delTarget.impact.has_data && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  چون دادهٔ ثبت‌شده دارد، پینِ مدیریتی لازم است:
                </Text>
                <Input.Password value={delPin} onChange={(e) => setDelPin(e.target.value)}
                  placeholder="پینِ مدیریتی" autoComplete="off" />
              </div>
            )}
          </div>
        )}
      </Modal>

    </div>
  );
}
